/**
 * AtlasSkyglowModule — computes the artificial skyglow over the camera (the night-sky realism wave, 2026-10-06) and
 * publishes it to `lib/light_pollution_live.ts` for every reader: the sky shader (the zenith glow, the light domes, the
 * stars' limiting magnitude), the city-light carpets, the haze and the clouds' undersides. The model is
 * `lib/light_pollution.ts` (Walker's law per settlement, calibrated per tier). RenderSub/3 — before the sky (RenderSub
 * 6) and everything that reads it. ~4 Hz (a 10 km/s ship moves 2.5 km between updates — the domes' bearings drift by
 * well under a degree at a city's distance); the sources are rebuilt when the site list or the world changes.
 *
 * PLAYER CITIES (agent P, 2026-10-06): every live player city (a `city` row, active or provisional) is a `player`
 * source `p<cityId>`, sized by its rank and what it has built (light_pollution `playerCityGlowSite`), tinted by its
 * allegiance; an invalid city is dark. The list is re-read only when the `city` / `structure` / `city_allegiance`
 * stamps move (lib/player_city_lights — never a per-frame walk), and the source array is replaced only when a player
 * source actually changed (the clouds' uplight provider is re-installed per array).
 *
 * Commons only: other worlds (personal, group, space) have no settlements and keep a pristine sky.
 * Diag: `__skyglow()` — the glow (ratio, SQM, Bortle, NELM, tint), the domes, the source count, the player sources;
 * lever `__skyglowAt(x, z)` evaluates any point (absolute metres).
 */
import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import { Phase, makeSystem } from '../engine-compat.js';
import { atlas } from '../state.js';
import { civilizationOf } from '../lib/biome_settlements.js';
import { lightSourcesFor, skyglowAt, domesAt, type GlowSite, type LightSource } from '../lib/light_pollution.js';
import { setSkyglowLive, skyglowLive } from '../lib/light_pollution_live.js';
import { gatherPlayerCities, playerGlowSites, playerCitiesStamp, ensurePlayerCitySubs, type PlayerCityDb } from '../lib/player_city_lights.js';

const UPDATE_MS = 250;
const DOMES_K = 4;
/** An unpatched table (stamp NaN) re-reads the player cities on this clock instead (ms). */
const PLAYER_UNPATCHED_MS = 5000;

interface SiteLike { id: number; centerX: number; centerZ: number; tier: string; siteSeed: number }

export class AtlasSkyglowModule implements IModule {
  readonly name = 'Skyglow';
  private app!: App;
  private lastAt = 0;
  private sources: LightSource[] = [];
  private sitesRef: unknown = null;
  private settleGlow: GlowSite[] = [];
  private playerStamp = '';
  private playerAt = 0;
  private playerSig = '';
  private playerSites: GlowSite[] = [];
  private readonly cam = new THREE.Vector3();

  init(app: App): void {
    this.app = app;
    app.world.addSystem(makeSystem('AtlasSkyglow', Phase.RenderSub, 3, () => this.tick()));
    const gl = globalThis as Record<string, unknown>;
    gl.__skyglow = () => {
      const s = skyglowLive();
      return { active: s.active, at: [Math.round(s.x), Math.round(s.z)], sources: s.sources.length, glow: s.glow,
        domes: s.domes.map((d) => ({ id: d.id, azDeg: +(d.az * 180 / Math.PI).toFixed(1), km: +d.dist.toFixed(2), peak: +d.peak.toFixed(3), halfWidthDeg: +(d.halfWidth * 180 / Math.PI).toFixed(1), heightDeg: +(d.height * 180 / Math.PI).toFixed(1) })),
        players: s.sources.filter((q) => q.id.startsWith('p')).map((q) => ({ id: q.id, x: Math.round(q.x), z: Math.round(q.z), A: +q.A.toFixed(3), r: +q.r.toFixed(3), tint: q.tint })) };
    };
    gl.__skyglowAt = (x: number, z: number) => skyglowAt(x, z, this.sources);
  }

  private siteList(): SiteLike[] | null {
    const m = this.app.getModule?.('CommonsSettlements') as unknown as { sites?: SiteLike[] | null } | undefined;
    return m?.sites ?? null;
  }

  /** The player cities' sites, re-read when a stamp moved; true when the source list must be rebuilt. */
  private refreshPlayers(now: number): boolean {
    const conn = atlas.conn as unknown as { db?: PlayerCityDb } | null;
    if (!conn?.db) return false;
    ensurePlayerCitySubs(conn);
    const stamp = playerCitiesStamp(conn.db);
    const unpatched = stamp.includes('NaN');
    if (unpatched ? now - this.playerAt < PLAYER_UNPATCHED_MS : stamp === this.playerStamp) return false;
    this.playerStamp = stamp; this.playerAt = now;
    const sites = playerGlowSites(gatherPlayerCities(conn.db, atlas.commonsWorldId));
    const sig = sites.map((s) => `${s.id}:${s.centerX.toFixed(1)},${s.centerZ.toFixed(1)}:${(s.aScale ?? 1).toFixed(4)}:${(s.rScale ?? 1).toFixed(4)}:${s.civ ?? ''}`).join(';');
    if (sig === this.playerSig) return false;
    this.playerSig = sig; this.playerSites = sites;
    return true;
  }

  private tick(): void {
    const now = performance.now();
    if (now - this.lastAt < UPDATE_MS) return;
    this.lastAt = now;
    const inCommons = atlas.currentWorldId != null && atlas.currentWorldId === atlas.commonsWorldId && !atlas.spaceFlight;
    const sites = inCommons ? this.siteList() : null;
    if (!sites) {
      if (skyglowLive().active || this.sources.length) { this.sources = []; this.sitesRef = null; this.playerSig = ''; this.playerStamp = ''; this.playerSites = []; setSkyglowLive({ ...skyglowLive(), active: false, sources: [], domes: [], glow: skyglowAt(0, 0, []) }); }
      return;
    }
    let rebuild = false;
    if (sites !== this.sitesRef) {
      this.sitesRef = sites;
      this.settleGlow = sites.map((s) => ({ id: s.id, centerX: s.centerX, centerZ: s.centerZ, tier: s.tier, civ: civilizationOf(s.siteSeed) as 'a' | 'b' }));
      rebuild = true;
    }
    if (this.refreshPlayers(now)) rebuild = true;
    if (rebuild) this.sources = lightSourcesFor(this.playerSites.length ? [...this.settleGlow, ...this.playerSites] : this.settleGlow);
    // The camera's ABSOLUTE position (render space minus the FP64 rebase shift on the scene).
    const camera = this.app.renderer.cameras.main;
    if (!camera) return;
    camera.getWorldPosition(this.cam);
    const sp = this.app.world.scene.position;
    const x = this.cam.x - sp.x, z = this.cam.z - sp.z;
    setSkyglowLive({ active: true, x, z, sources: this.sources, glow: skyglowAt(x, z, this.sources), domes: domesAt(x, z, this.sources, DOMES_K) });
  }
}
